import type {
  StripeAccountSettings,
  StripeCheckoutSettings,
} from "schemas/generated/stripeConnect";

import * as React from "react";

import { LabeledControl } from "@replo/design-system/blocks/labeled-control/LabeledControl";
import { SelectionItem } from "@replo/design-system/blocks/selection/SelectionItem";
import { Button } from "@replo/design-system/components/button/Button";
import { Checkbox } from "@replo/design-system/components/checkbox/Checkbox";
import { Input } from "@replo/design-system/components/input/Input";
import {
  PopoverContent,
  PopoverRoot,
  PopoverTrigger,
} from "@replo/design-system/components/popover/Popover";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { Tag } from "@replo/design-system/components/tag/Tag";
import { toast } from "@replo/design-system/components/toast/Toast";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import { skipToken } from "@tanstack/react-query";
import LogoBadge from "@v2/components/LogoBadge";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import {
  integrationRouter,
  integrationUtils,
} from "@v2/trpc/routes/integration";
import {
  stripeConnectRouter,
  stripeConnectUtils,
} from "@v2/trpc/routes/stripeConnect";
import {
  ArrowUpRight,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Link2Off,
} from "lucide-react";
import { useLocation } from "react-router-dom";

// Shipping address collection component
const ShippingAddressCollection: React.FC<{
  allowedCountries: string[] | null;
  onChange: (countries: string[] | null) => void;
}> = ({ allowedCountries, onChange }) => {
  const countries = [
    { code: "US", name: "United States" },
    { code: "CA", name: "Canada" },
    { code: "GB", name: "United Kingdom" },
    { code: "AU", name: "Australia" },
    { code: "DE", name: "Germany" },
    { code: "FR", name: "France" },
    { code: "IT", name: "Italy" },
    { code: "ES", name: "Spain" },
    { code: "NL", name: "Netherlands" },
    { code: "SE", name: "Sweden" },
    { code: "NO", name: "Norway" },
    { code: "DK", name: "Denmark" },
    { code: "FI", name: "Finland" },
    { code: "JP", name: "Japan" },
    { code: "KR", name: "South Korea" },
    { code: "SG", name: "Singapore" },
    { code: "HK", name: "Hong Kong" },
    { code: "NZ", name: "New Zealand" },
    { code: "CH", name: "Switzerland" },
    { code: "AT", name: "Austria" },
    { code: "BE", name: "Belgium" },
    { code: "IE", name: "Ireland" },
    { code: "PT", name: "Portugal" },
    { code: "PL", name: "Poland" },
    { code: "CZ", name: "Czech Republic" },
    { code: "HU", name: "Hungary" },
    { code: "SK", name: "Slovakia" },
    { code: "SI", name: "Slovenia" },
    { code: "HR", name: "Croatia" },
    { code: "RO", name: "Romania" },
    { code: "BG", name: "Bulgaria" },
    { code: "GR", name: "Greece" },
    { code: "CY", name: "Cyprus" },
    { code: "MT", name: "Malta" },
    { code: "LU", name: "Luxembourg" },
    { code: "EE", name: "Estonia" },
    { code: "LV", name: "Latvia" },
    { code: "LT", name: "Lithuania" },
  ];

  const selectedCountries = allowedCountries ?? [];
  const isCollecting = selectedCountries.length > 0;

  const handleCountryToggle = (countryCode: string, checked: boolean) => {
    if (checked) {
      const newCountries = [...selectedCountries, countryCode];
      onChange(newCountries.length === 0 ? null : newCountries);
    } else {
      const newCountries = selectedCountries.filter(
        (code) => code !== countryCode,
      );
      onChange(newCountries.length === 0 ? null : newCountries);
    }
  };

  const handleDontCollectToggle = (checked: boolean) => {
    if (checked) {
      onChange(null);
    } else {
      onChange([]);
    }
  };

  const getDisplayText = () => {
    if (!isCollecting) {
      return "Don't collect";
    }
    if (selectedCountries.length === 1) {
      const country = countries.find((c) => c.code === selectedCountries[0]);
      return country?.name ?? "1 country selected";
    }
    return `${selectedCountries.length} countries selected`;
  };

  return (
    <PopoverRoot>
      <PopoverTrigger
        render={
          <Button
            variant="secondary"
            size="base"
            endEnhancer={<ChevronDown size={16} className="text-muted" />}
            UNSAFE_className="justify-between w-full"
          />
        }
      >
        {getDisplayText()}
      </PopoverTrigger>
      <PopoverContent showCloseButton={false} UNSAFE_className="w-72">
        <div className="space-y-3">
          <SelectionItem
            type="checkbox"
            id="dont-collect-shipping"
            label="Do not collect shipping address"
            description="Skip shipping address collection during checkout"
            checked={!isCollecting}
            onCheckedChange={handleDontCollectToggle}
          />

          <div className="space-y-2 max-h-60 overflow-y-auto">
            {countries.map((country) => (
              <div key={country.code} className="flex items-center space-x-2">
                <Checkbox
                  checked={selectedCountries.includes(country.code)}
                  onCheckedChange={(checked) => {
                    return handleCountryToggle(
                      country.code,
                      checked as boolean,
                    );
                  }}
                />
                <span className="text-xs">{country.name}</span>
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </PopoverRoot>
  );
};

// Settings form component
const SettingsPanel: React.FC<{
  settings: {
    accountSettings?: StripeAccountSettings;
    checkoutSettings?: StripeCheckoutSettings;
  } | null;
  onUpdateSettings: (settings: {
    accountSettings?: StripeAccountSettings;
    checkoutSettings?: StripeCheckoutSettings;
  }) => void;
  isLoading: boolean;
}> = ({ settings, onUpdateSettings, isLoading }) => {
  const [isCheckoutSettingsExpanded, setIsCheckoutSettingsExpanded] =
    React.useState(false);
  const [localSettings, setLocalSettings] = React.useState<{
    accountSettings: Partial<StripeAccountSettings>;
    checkoutSettings: Partial<StripeCheckoutSettings>;
  }>({
    accountSettings: settings?.accountSettings ?? {},
    checkoutSettings: settings?.checkoutSettings ?? {
      automaticTax: false,
      allowPromotionCodes: false,
    },
  });

  React.useEffect(() => {
    setLocalSettings({
      accountSettings: settings?.accountSettings ?? {},
      checkoutSettings: settings?.checkoutSettings ?? {
        automaticTax: false,
        allowPromotionCodes: false,
      },
    });
  }, [settings]);

  const handleSave = () => {
    onUpdateSettings({
      accountSettings: {
        ...localSettings.accountSettings,
        payoutSchedule: {
          interval: "monthly",
          monthlyAnchor: 1,
        },
      } as StripeAccountSettings,
      checkoutSettings:
        localSettings.checkoutSettings as StripeCheckoutSettings,
    });
  };

  const updateAccountSetting = (key: string, value: any) => {
    setLocalSettings((prev) => ({
      ...prev,
      accountSettings: {
        ...prev.accountSettings,
        [key]: value,
      },
    }));
  };

  const updateCheckoutSetting = (key: string, value: any) => {
    setLocalSettings((prev) => ({
      ...prev,
      checkoutSettings: {
        ...prev.checkoutSettings,
        [key]: value,
      },
    }));
  };

  return (
    <div className="space-y-4">
      {/* Settings Collapsible Section */}
      <div className="border-0.5 border-border rounded-xl">
        <button
          type="button"
          className="w-full flex items-center justify-between p-4 text-left hover:bg-sheet transition-colors"
          onClick={() => {
            return setIsCheckoutSettingsExpanded(!isCheckoutSettingsExpanded);
          }}
        >
          <h4 className="text-sm font-medium">Checkout settings</h4>
          {isCheckoutSettingsExpanded ? (
            <ChevronUp size={16} className="text-muted" />
          ) : (
            <ChevronDown size={16} className="text-muted" />
          )}
        </button>

        {isCheckoutSettingsExpanded && (
          <div className="px-4 pb-4 space-y-4">
            {/* Statement Descriptor */}
            <LabeledControl
              label="Statement Descriptor"
              description="Will appear on customers bank statements"
              size="base"
            >
              <Input
                value={localSettings.accountSettings?.statementDescriptor ?? ""}
                onChange={(e) => {
                  return updateAccountSetting(
                    "statementDescriptor",
                    e.target.value,
                  );
                }}
                placeholder="Studs"
                maxLength={22}
                size="base"
              />
            </LabeledControl>

            {/* Collect address at checkout */}
            <LabeledControl label="Collect address at checkout" size="base">
              <ShippingAddressCollection
                allowedCountries={
                  localSettings.checkoutSettings?.allowedCountries ?? null
                }
                onChange={(countries) => {
                  return updateCheckoutSetting("allowedCountries", countries);
                }}
              />
            </LabeledControl>

            {/* Automatic Tax */}
            <SelectionItem
              type="checkbox"
              id="automatic-tax"
              label="Enable automatic tax calculation"
              description="Will automatically calculate tax based on customer location"
              checked={localSettings.checkoutSettings?.automaticTax ?? false}
              onCheckedChange={(checked) => {
                return updateCheckoutSetting("automaticTax", checked);
              }}
            />

            {/* Save Button */}
            <div className="flex justify-end pt-2">
              <Button
                variant="primary"
                size="base"
                onClick={handleSave}
                loading={isLoading}
              >
                Save
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const StripeConnectIntegration: React.FC<{
  variant?: "black-box" | "white-box";
}> = ({ variant = "white-box" }) => {
  const projectId = useCurrentProjectId();
  const location = useLocation();
  const [isConnecting, setIsConnecting] = React.useState(false);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [settings, setSettings] = React.useState<{
    accountSettings?: StripeAccountSettings;
    checkoutSettings?: StripeCheckoutSettings;
  } | null>(null);

  // Get Stripe Connect onboarding status
  const { data: onboardingStatus, isLoading } =
    stripeConnectRouter.stripeConnectGetOnboardingStatus.useQuery(
      projectId ? { projectId } : skipToken,
    );

  // Get all integrations to find the Stripe Connect integration ID
  const { data: integrations } = integrationRouter.find.useQuery(
    projectId ? { projectId } : skipToken,
  );

  // Find the Stripe Connect integration
  const stripeIntegration = integrations?.find(
    (integration) => integration.type === "StripeConnect",
  );

  // Get Stripe Connect settings
  const { data: stripeSettings } =
    stripeConnectRouter.stripeConnectGetSettings.useQuery(
      projectId ? { projectId } : skipToken,
    );

  // Create onboarding link mutation
  const createOnboardingLink =
    stripeConnectRouter.stripeConnectCreateOnboardingLink.useMutation({
      onSuccess: (data) => {
        window.open(data.onboardingUrl, "_blank");
        setIsConnecting(true);
      },
      onError: () => {
        setIsConnecting(false);
        toast.error({
          title: "Setup Failed",
          description: "Failed to create Stripe Connect onboarding link.",
        });
      },
    });

  // Create finish setup link mutation
  const createFinishSetupLink =
    stripeConnectRouter.stripeConnectCreateFinishSetupLink.useMutation({
      onSuccess: (data) => {
        window.open(data.finishSetupUrl, "_blank");
        setIsConnecting(true);
        toast.success({
          title: "Stripe Connect Setup",
          description: "Redirected to Stripe to complete setup.",
        });
      },
      onError: () => {
        setIsConnecting(false);
        toast.error({
          title: "Setup Failed",
          description: "Failed to create Stripe Connect finish setup link.",
        });
      },
    });

  // Create dashboard login link mutation
  const createDashboardLoginLink =
    stripeConnectRouter.stripeConnectCreateDashboardLoginLink.useMutation({
      onSuccess: (data) => {
        window.open(data.dashboardUrl, "_blank");
      },
      onError: () => {
        toast.error({
          title: "Dashboard Access Failed",
          description: "Failed to create Stripe dashboard login link.",
        });
      },
    });

  // Refresh onboarding status mutation
  const refreshOnboardingStatus =
    stripeConnectRouter.stripeConnectRefreshOnboardingStatus.useMutation({
      onSuccess: (data) => {
        if (data.wasUpdated && data.onboardingCompleted) {
          // Only invalidate and show success when onboarding is actually completed
          void stripeConnectUtils.stripeConnectGetOnboardingStatus.invalidate();
          toast.success({
            title: "Setup Complete!",
            description:
              "Stripe Connect onboarding has been completed successfully.",
          });
        } else if (data.wasUpdated) {
          // Status was updated but still not complete - just invalidate without toast
          void stripeConnectUtils.stripeConnectGetOnboardingStatus.invalidate();
        }
        // If no update, don't invalidate to prevent unnecessary refetches
      },
      onError: () => {
        toast.error({
          title: "Status Check Failed",
          description:
            "Failed to refresh Stripe Connect status. Please try again.",
        });
      },
    });

  // Delete integration mutation
  const deleteIntegration = integrationRouter.delete.useMutation({
    onSuccess: () => {
      toast.success({
        title: "Stripe Disconnected",
        description:
          "Stripe Connect has been successfully disconnected from your project.",
      });
      // Invalidate queries to refresh the UI
      void integrationUtils.find.invalidate();
      void stripeConnectUtils.stripeConnectGetOnboardingStatus.invalidate();
    },
    onError: () => {
      toast.error({
        title: "Disconnect Failed",
        description: "Failed to disconnect Stripe Connect. Please try again.",
      });
    },
  });

  // Update settings mutation
  const updateSettings =
    stripeConnectRouter.stripeConnectUpdateSettings.useMutation({
      onSuccess: () => {
        toast.success({
          title: "Settings Updated",
          description:
            "Stripe Connect settings have been updated successfully.",
        });
        void stripeConnectUtils.stripeConnectGetSettings.invalidate();
      },
      onError: () => {
        toast.error({
          title: "Update Failed",
          description:
            "Failed to update Stripe Connect settings. Please try again.",
        });
      },
    });

  const handleConnect = () => {
    setIsConnecting(true);
    createOnboardingLink.mutate({ projectId: projectId! });
  };

  const handleFinishSetup = () => {
    setIsConnecting(true);
    createFinishSetupLink.mutate({ projectId: projectId! });
  };

  const handleOpenDashboard = () => {
    createDashboardLoginLink.mutate({ projectId: projectId! });
  };

  const handleDisconnect = () => {
    if (!stripeIntegration || !projectId) {
      return;
    }

    deleteIntegration.mutate({
      integrationId: stripeIntegration.id,
      projectId: projectId,
    });
  };

  const handleUpdateSettings = (newSettings: {
    accountSettings?: StripeAccountSettings;
    checkoutSettings?: StripeCheckoutSettings;
  }) => {
    if (!projectId) {
      return;
    }

    updateSettings.mutate({
      projectId,
      accountSettings: newSettings.accountSettings,
      checkoutSettings: newSettings.checkoutSettings,
    });
  };

  // Update settings state when data loads
  React.useEffect(() => {
    if (stripeSettings) {
      setSettings(stripeSettings);
    }
  }, [stripeSettings]);

  // Auto-refresh onboarding status once when component mounts if partially connected
  const hasAutoRefreshed = React.useRef(false);

  React.useEffect(() => {
    if (
      projectId &&
      onboardingStatus?.isConnected &&
      !onboardingStatus?.onboardingCompleted &&
      !isRefreshing &&
      !isConnecting &&
      !hasAutoRefreshed.current &&
      !refreshOnboardingStatus.isPending
    ) {
      hasAutoRefreshed.current = true;
      setIsRefreshing(true);
      refreshOnboardingStatus.mutate(
        { projectId },
        {
          onSettled: () => setIsRefreshing(false),
        },
      );
    }
  }, [
    projectId,
    onboardingStatus?.isConnected,
    onboardingStatus?.onboardingCompleted,
    isRefreshing,
    isConnecting,
    refreshOnboardingStatus,
  ]);

  if (isLoading || isRefreshing) {
    return <Skeleton className="h-[74px] w-full" />;
  }

  const isConnected = onboardingStatus?.isConnected ?? false;
  const isOnboardingComplete = onboardingStatus?.onboardingCompleted ?? false;

  const isSettingsContext = location.pathname.includes("/settings/");

  // For black box variant when not connected, show simple setup UI
  if (variant === "black-box" && !isConnected) {
    return (
      <div className="flex items-center justify-between p-4 border-0.5 border-border rounded-xl bg-black text-white">
        <div className="flex items-center gap-3">
          <div className="bg-white rounded-2xl w-10 h-10 shrink-0 overflow-hidden">
            <LogoBadge className="w-full h-full" />
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-medium text-white">
              {isSettingsContext ? "Replo" : "Set up payments"}
            </span>
            <span className="text-xs text-sheet">
              Host products, sell them, and get paid through Replo.
            </span>
          </div>
        </div>
        <Button
          variant="secondary"
          size="base"
          onClick={handleConnect}
          loading={
            isConnecting || createOnboardingLink.isPending || isRefreshing
          }
          endEnhancer={<ArrowUpRight />}
          UNSAFE_className="bg-white text-black hover:bg-gray-100"
        >
          Set up
        </Button>
      </div>
    );
  }

  // For black box variant when partially connected, show white box with limited controls
  if (variant === "black-box" && isConnected && !isOnboardingComplete) {
    return (
      <div className="flex items-center justify-between p-4 border-0.5 border-border rounded-xl bg-white">
        <div className="flex items-center gap-3">
          <div className="border-0.5 border-border rounded-2xl w-10 h-10 shrink-0 overflow-hidden">
            <LogoBadge className="w-full h-full" />
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-medium">Pay on Replo</span>
            <span className="text-xs text-default">
              Setup incomplete - finish onboarding to accept payments
            </span>
          </div>
        </div>
        <Button
          variant="primary"
          size="base"
          onClick={handleFinishSetup}
          loading={
            isConnecting || createFinishSetupLink.isPending || isRefreshing
          }
        >
          Complete Setup
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {isConnected ? (
        <>
          {/* Replo Integration Card */}
          <div className="p-4 border-0.5 border-border rounded-xl">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="border-0.5 border-border rounded-2xl w-10 h-10 shrink-0 overflow-hidden">
                  <LogoBadge className="w-full h-full" />
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm font-medium">
                    {isSettingsContext ? "Replo" : "Set up payments"}
                  </span>
                  <span className="text-xs text-muted">
                    Host products, sell them, and get paid through Replo.
                  </span>
                </div>
              </div>
              <Button
                variant="secondary"
                size="base"
                endEnhancer={<ExternalLink />}
                onClick={handleOpenDashboard}
                loading={createDashboardLoginLink.isPending}
              >
                Manage Payments
              </Button>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-xs text-muted">
                {isOnboardingComplete
                  ? "Ready to accept payments"
                  : "Setup incomplete - finish onboarding to accept payments"}
              </span>
              <div className="flex items-center gap-2">
                <Tooltip content="Disconnect Stripe" triggerAsChild>
                  <Button
                    icon
                    variant="secondary"
                    size="sm"
                    onClick={handleDisconnect}
                    loading={deleteIntegration.isPending}
                  >
                    <Link2Off />
                  </Button>
                </Tooltip>
                <Tag
                  variant={isOnboardingComplete ? "success" : "info"}
                  size="base"
                >
                  {isOnboardingComplete ? "Connected" : "Setup Required"}
                </Tag>
              </div>
            </div>

            {!isOnboardingComplete && (
              <div className="flex justify-start mt-3">
                <Button
                  variant="primary"
                  size="base"
                  onClick={handleFinishSetup}
                  loading={
                    isConnecting ||
                    createFinishSetupLink.isPending ||
                    isRefreshing
                  }
                >
                  Complete Setup
                </Button>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="p-4 border-0.5 border-border rounded-xl bg-surface">
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium">
                  Connect Stripe Account
                </span>
                <span className="text-xs text-sheet">
                  Set up Stripe to start accepting payments on your pages.
                  You'll be redirected to Stripe to complete the setup process.
                </span>
              </div>

              <div className="flex justify-start">
                <Button
                  variant="primary"
                  size="base"
                  onClick={handleConnect}
                  loading={
                    isConnecting ||
                    createOnboardingLink.isPending ||
                    isRefreshing
                  }
                >
                  Connect Stripe
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Checkout Settings - Separate card below Replo integration */}
      <SettingsPanel
        settings={settings}
        onUpdateSettings={handleUpdateSettings}
        isLoading={updateSettings.isPending}
      />
    </div>
  );
};
